import {
  DismissableNudge,
  useResetDismissableNudge,
} from "@/components/interactive/DismissableNudge";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useAgentWorkspace } from "./AgentSidebarContext";
import { ASSISTANT_RAIL_HEIGHT } from "./ChalkAssistantPanel";
import { createConversation } from "@/gen_src/proto/chalk/agent/v1/conversation-AgentConversationService_connectquery";
import { useHasPermissionForEndpoint } from "@/utils/hooks/use-permissions";
import { AmplitudeEvent, useAmplitudeTrack } from "@/utils/amplitude";
import React from "react";

/** Clears the stored dismissal so the card shows again; for the test-components pane. */
export const useResetAssistantNudge = () => {
  return useResetDismissableNudge(LocalStorageKey.ASSISTANT_NUDGE);
};

/**
 * Announces the assistant to users who have not used it: a card in the bottom-right corner, just
 * above the rail and its "+ Assistant" button, offering to start a chat. It stays away once
 * dismissed, and counts opening a chat by any route as having found the feature.
 */
export const ChalkAssistantNudge: React.FC = () => {
  const workspace = useAgentWorkspace();
  // Starting a chat calls CreateConversation, so a user who cannot do that gets no invitation.
  const { hasPermission: canStartChat, loaded: permissionLoaded } =
    useHasPermissionForEndpoint(createConversation);
  const track = useAmplitudeTrack();
  const hasChats = (workspace?.state.chats.length ?? 0) > 0;
  return (
    <DismissableNudge
      id="chalk-assistant"
      title="Meet Chalk Assistant"
      description="Ask about your features, resolvers, queries and data from any page. Chats open in windows along the bottom rail, so you can keep working while you talk."
      actions={[
        {
          buttonText: "Start a chat",
          buttonOnClick: () => {
            track(AmplitudeEvent.AI_ASSISTANT_OPEN, { surface: "assistant_nudge" });
            workspace?.newChat();
          },
        },
      ]}
      storageKey={LocalStorageKey.ASSISTANT_NUDGE}
      shouldShow={workspace?.railBounds != null && permissionLoaded && canStartChat}
      dismissWhen={hasChats}
      placement="bottom-right"
      bottomOffset={ASSISTANT_RAIL_HEIGHT}
    />
  );
};
